import { useCallback, useEffect, useRef, useState } from "react";
import { generateAssetUrl, getAssetFileName } from "../../utils/common.js";

/*
 * Thumbnail and file rules for one tutorial row.
 * A new file replaces the preview object URL and is written back onto the video.
 * A stored videoUrl is shown through generateAssetUrl and never treated as a File.
 * When neither a file nor a URL is present, the preview and the displayed name are cleared.
 * The upload control shows the File itself, or a zero-size name taken from the stored URL.
 * Choosing a file from the picker and dropping a file share that same write path.
 * Dropped files are accepted only when the MIME type starts with "video/".
 * Other dropped types leave the current file and preview unchanged.
 * An empty thumbnail field stays an empty string until blur, which stores 0.
 * Null and undefined on blur also store 0. A non-numeric thumbnail is ignored.
 * Values are clamped to zero and, when duration is known, to the floored duration.
 * Seek runs when the thumbnail second changes, including when duration is unusable.
 * Seek is skipped when the second is missing, blank, or equal to the last sought value.
 * The last sought second is recorded only after a ready player accepts currentTime.
 * Ready means readyState is at least HAVE_CURRENT_DATA, which is 2.
 * If the player is not ready, seek waits for canplay and does not record the second yet.
 * The canplay listener is removed by the effect cleanup and by the handler itself.
 * Loaded metadata stores duration only when it is positive, finite, and not NaN.
 * A thumbnail past that duration is written back with Math.floor before the seek.
 * The seek after metadata still uses Math.min with Infinity when duration is missing.
 * That seek is not skipped for an unusable duration.
 * The source effect must not list createVideoPreview as a dependency.
 * Doing so would revoke the object URL that the effect just created.
 * Unmount pauses the player, resets currentTime to 0, and revokes the first preview.
 * That cleanup intentionally ignores later preview changes.
 */

/** HTMLMediaElement.HAVE_CURRENT_DATA. Seeking is valid from this readyState on. */
const HAVE_CURRENT_DATA = 2;

/**
 * Chooses what the upload control shows: the picked file, a name from the stored URL, or nothing.
 * @param {File|null} videoFile
 * @param {string|null} fileNameFromUrl
 * @returns {File|{name: string, size: number}|null}
 */
const resolveUploadFile = (videoFile, fileNameFromUrl) => {
  if (videoFile) {
    return videoFile;
  } else if (fileNameFromUrl) {
    return { name: fileNameFromUrl, size: 0 };
  } else {
    return null;
  }
};

/**
 * A duration can be used to clamp the thumbnail only when it is a positive finite number.
 * Zero, NaN, and Infinity leave the existing seek path in place.
 * @param {number} duration
 * @returns {boolean}
 */
const hasPlayableDuration = (duration) =>
  Boolean(duration) && !isNaN(duration) && isFinite(duration);

/**
 * Keeps a thumbnail second at zero or above, and inside the clip when duration is known.
 * @param {number} second
 * @param {number|null} duration
 * @returns {number}
 */
const clampThumbnailToDuration = (second, duration) => {
  let validatedValue = Math.max(0, second || 0);
  if (duration !== null && validatedValue > duration) {
    validatedValue = Math.floor(duration);
  }
  return validatedValue;
};

/**
 * Seeks the preview to the thumbnail second when that field changes.
 * Returns a cleanup when the video is not ready yet.
 */
const seekPreviewToThumbnail = ({
  videoRef,
  videoPreview,
  thumbnailSecond,
  lastThumbnailSecondRef,
  videoDuration,
}) => {
  const shouldSeek =
    videoRef.current &&
    videoPreview &&
    thumbnailSecond !== undefined &&
    thumbnailSecond !== "" &&
    lastThumbnailSecondRef.current !== thumbnailSecond;

  if (!shouldSeek) {
    return undefined;
  }

  const seekTime = clampThumbnailToDuration(thumbnailSecond, videoDuration);
  if (videoRef.current.readyState >= HAVE_CURRENT_DATA) {
    videoRef.current.currentTime = seekTime;
  } else {
    const handleCanPlay = () => {
      if (videoRef.current) {
        videoRef.current.currentTime = seekTime;
        videoRef.current.removeEventListener("canplay", handleCanPlay);
      }
    };
    videoRef.current.addEventListener("canplay", handleCanPlay);
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener("canplay", handleCanPlay);
      }
    };
  }

  lastThumbnailSecondRef.current = thumbnailSecond;
  return undefined;
};

/**
 * Points the preview at a newly picked file, a stored asset URL, or an empty slot.
 * The empty slot clears both the file and the object URL so a removed video does not linger.
 */
const applyVideoSource = ({
  video,
  createVideoPreview,
  setVideoFile,
  setVideoPreview,
  setFileNameFromUrl,
}) => {
  if (video?.videoFile) {
    setVideoFile(video.videoFile);
    createVideoPreview(video.videoFile);
    setFileNameFromUrl(null);
  } else if (video?.videoUrl) {
    setVideoFile(null);
    setVideoPreview(generateAssetUrl(video.videoUrl));
    setFileNameFromUrl(getAssetFileName(video.videoUrl) || null);
  } else {
    setVideoFile(null);
    setVideoPreview(null);
    setFileNameFromUrl(null);
  }
};

/**
 * Stores duration once metadata loads, then seeks to the thumbnail second.
 * Seeking still runs when duration is missing so an unusable length does not skip the preview.
 */
const applyLoadedMetadata = ({
  videoElement,
  video,
  index,
  onVideoChange,
  setVideoDuration,
}) => {
  if (!videoElement) {
    return;
  }

  const duration = videoElement.duration;
  if (hasPlayableDuration(duration)) {
    setVideoDuration(duration);

    if (video?.thumbnailSecond !== undefined) {
      const currentSecond = video.thumbnailSecond || 0;
      if (currentSecond > duration) {
        onVideoChange(index, {
          ...video,
          thumbnailSecond: Math.floor(duration),
        });
      }
    }
  }

  if (video?.thumbnailSecond !== undefined) {
    const seekTime = Math.min(video.thumbnailSecond || 0, duration || Infinity);
    videoElement.currentTime = Math.max(0, seekTime);
  }
};

/** Replaces the object URL when the chosen value is a real File. Other values leave the preview alone. */
const replaceVideoPreview = (file, videoPreview, setVideoPreview) => {
  if (file && file instanceof File) {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoPreview(URL.createObjectURL(file));
  }
};

/** Gives the parent the player ref once the preview element exists. */
const publishVideoRef = (onVideoRef, videoRef) => {
  if (onVideoRef && videoRef.current) {
    onVideoRef(videoRef);
  }
};

/** Pauses the mounted player and revokes the preview captured when the effect was created. */
const releaseVideoPreview = (videoRef, videoPreview) => {
  if (videoRef.current) {
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  }
  if (videoPreview) {
    URL.revokeObjectURL(videoPreview);
  }
};

const rememberChosenVideo = (file, setVideoFile, createVideoPreview, onVideoChange, index, video) => {
  setVideoFile(file);
  createVideoPreview(file);
  onVideoChange(index, { ...video, videoFile: file });
};

const takeFirstSelectedFile = (event, rememberVideoFile) => {
  const file = event.target.files?.[0];
  if (file) {
    rememberVideoFile(file);
  }
};

const acceptDroppedVideo = (event, setIsDragging, rememberVideoFile) => {
  event.preventDefault();
  setIsDragging(false);
  const file = event.dataTransfer.files?.[0];
  if (file && file.type.startsWith("video/")) {
    rememberVideoFile(file);
  }
};

const changeThumbnailSecond = (event, index, video, videoDuration, onVideoChange) => {
  const value = event.target.value;
  if (value === "") {
    onVideoChange(index, { ...video, thumbnailSecond: "" });
    return;
  }

  const numValue = parseInt(value, 10);
  if (isNaN(numValue)) {
    return;
  }

  onVideoChange(index, {
    ...video,
    thumbnailSecond: clampThumbnailToDuration(numValue, videoDuration),
  });
};

const blurThumbnailSecond = (event, index, video, onVideoChange) => {
  const value = event.target.value;
  if (value === "" || value === null || value === undefined) {
    onVideoChange(index, { ...video, thumbnailSecond: 0 });
  }
};

const changeVideoField = (event, index, video, field, onVideoChange) => {
  onVideoChange(index, { ...video, [field]: event.target.value });
};

/** File, text, and thumbnail callbacks for one video row. */
const useVideoInputHandlers = ({
  video,
  index,
  onVideoChange,
  videoPreview,
  videoDuration,
  videoRef,
  setVideoFile,
  setVideoPreview,
  setIsDragging,
  setVideoDuration,
}) => {
  const createVideoPreview = useCallback(
    (file) => replaceVideoPreview(file, videoPreview, setVideoPreview),
    [videoPreview, setVideoPreview],
  );
  const rememberVideoFile = useCallback(
    (file) =>
      rememberChosenVideo(
        file,
        setVideoFile,
        createVideoPreview,
        onVideoChange,
        index,
        video,
      ),
    [createVideoPreview, index, onVideoChange, setVideoFile, video],
  );

  const handleFileChange = useCallback(
    (event) => takeFirstSelectedFile(event, rememberVideoFile),
    [rememberVideoFile],
  );
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, [setIsDragging]);
  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragging(false);
  }, [setIsDragging]);
  const handleDrop = useCallback(
    (event) => acceptDroppedVideo(event, setIsDragging, rememberVideoFile),
    [rememberVideoFile, setIsDragging],
  );
  const handleFileClick = useCallback(() => {
    document.getElementById(`videoFileInput-${index}`)?.click();
  }, [index]);
  const handleThumbnailSecondChange = useCallback(
    (event) =>
      changeThumbnailSecond(event, index, video, videoDuration, onVideoChange),
    [index, onVideoChange, video, videoDuration],
  );
  const handleThumbnailSecondBlur = useCallback(
    (event) => blurThumbnailSecond(event, index, video, onVideoChange),
    [index, onVideoChange, video],
  );
  const handleNameChange = useCallback(
    (event) => changeVideoField(event, index, video, "name", onVideoChange),
    [index, onVideoChange, video],
  );
  const handleDescriptionChange = useCallback(
    (event) =>
      changeVideoField(event, index, video, "description", onVideoChange),
    [index, onVideoChange, video],
  );
  const handleLoadedMetadata = useCallback(() => {
    applyLoadedMetadata({
      videoElement: videoRef.current,
      video,
      index,
      onVideoChange,
      setVideoDuration,
    });
  }, [index, onVideoChange, setVideoDuration, video, videoRef]);

  return {
    createVideoPreview,
    handleDescriptionChange,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileChange,
    handleFileClick,
    handleLoadedMetadata,
    handleNameChange,
    handleThumbnailSecondBlur,
    handleThumbnailSecondChange,
  };
};

/**
 * State and handlers for one video tutorial row: file, preview, and thumbnail second.
 * @param {Object} video
 * @param {number} index
 * @param {Function} onVideoChange
 * @param {Function} [onVideoRef]
 */
export const useVideoDetailsSection = ({
  video,
  index,
  onVideoChange,
  onVideoRef,
}) => {
  const [videoFile, setVideoFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [videoDuration, setVideoDuration] = useState(null);
  const [fileNameFromUrl, setFileNameFromUrl] = useState(null);
  const videoRef = useRef(null);
  const lastThumbnailSecondRef = useRef(video?.thumbnailSecond);

  const { createVideoPreview, ...handlers } = useVideoInputHandlers({
    video,
    index,
    onVideoChange,
    videoPreview,
    videoDuration,
    videoRef,
    setVideoFile,
    setVideoPreview,
    setIsDragging,
    setVideoDuration,
  });

  useEffect(() => {
    applyVideoSource({
      video,
      createVideoPreview,
      setVideoFile,
      setVideoPreview,
      setFileNameFromUrl,
    });
    // createVideoPreview closes over the current preview; re-running on preview changes would revoke the URL just created.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.videoFile, video?.videoUrl]);

  useEffect(() => {
    publishVideoRef(onVideoRef, videoRef);
  }, [onVideoRef, videoPreview]);

  useEffect(() => {
    return seekPreviewToThumbnail({
      videoRef,
      videoPreview,
      thumbnailSecond: video?.thumbnailSecond,
      lastThumbnailSecondRef,
      videoDuration,
    });
  }, [video?.thumbnailSecond, videoPreview, videoDuration]);

  useEffect(() => {
    return () => releaseVideoPreview(videoRef, videoPreview);
    // Only run cleanup on unmount, not when videoPreview changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUploadFile = resolveUploadFile(videoFile, fileNameFromUrl);

  return {
    fileInputId: `videoFileInput-${index}`,
    ...handlers,
    isDragging,
    selectedUploadFile,
    videoDuration,
    videoPreview,
    videoRef,
  };
};
